/**
 * Native bridge — one place that wires up every Capacitor plugin used by
 * iCaard's mobile shell. Kept intentionally thin: individual pages call the
 * exported helpers (e.g. `sharePayload`, `scanQR`, `secureSet`) and don't
 * touch Capacitor plugins directly, so the same code compiles for
 * web + Android + iOS.
 *
 * Web fallbacks: every helper degrades gracefully to a web-native API
 * (`navigator.share`, `localStorage`, `getUserMedia` etc.) when running
 * outside a Capacitor shell.
 */
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Clipboard } from '@capacitor/clipboard';
import { Device } from '@capacitor/device';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Network } from '@capacitor/network';
import { PushNotifications } from '@capacitor/push-notifications';
import { Share } from '@capacitor/share';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Toast } from '@capacitor/toast';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { BiometricAuth, BiometryType } from '@aparajita/capacitor-biometric-auth';
import { BarcodeScanner } from '@capacitor-mlkit/barcode-scanning';

export const isNative = () => Capacitor.isNativePlatform();
export const platform = () => Capacitor.getPlatform(); // 'web' | 'android' | 'ios'

// ---------------------------------------------------------------------------
// Boot — call once from index.js
// ---------------------------------------------------------------------------
export async function initNativeShell() {
  if (!isNative()) return;
  try {
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#09090F' });
  } catch (e) { /* iOS may reject setBackgroundColor — expected */ }
  try { await SplashScreen.hide({ fadeOutDuration: 400 }); } catch (_) {}

  // Hardware back button (Android) — send it to the browser history rather
  // than closing the app when there's a page to go back to.
  App.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) {
      window.history.back();
    } else {
      App.exitApp();
    }
  });

  // App URL open — used for deep-links (e.g. `icaard://wallet` or
  // `https://icaard.com/app/wallet` opened from a push notification).
  App.addListener('appUrlOpen', (evt) => {
    try {
      const url = new URL(evt.url);
      const path = url.pathname + url.search + url.hash;
      if (path && path !== window.location.pathname) {
        window.location.href = path;
      }
    } catch (_) { /* ignore malformed deep link */ }
  });
}

// ---------------------------------------------------------------------------
// Network — surface an offline banner + auto-refresh on reconnect
// ---------------------------------------------------------------------------
export function watchNetwork(cb): () => void {
  if (!isNative() && typeof window !== 'undefined') {
    const onOnline = () => cb(true);
    const onOffline = () => cb(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    cb(navigator.onLine);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }
  let handle;
  Network.addListener('networkStatusChange', (s) => cb(s.connected)).then((h) => (handle = h));
  Network.getStatus().then((s) => cb(s.connected));
  return () => { if (handle && handle.remove) handle.remove(); };
}

// ---------------------------------------------------------------------------
// Secure Storage — auth tokens, biometric preferences, refresh tokens
// ---------------------------------------------------------------------------
export async function secureSet(key, value) {
  if (!isNative()) {
    // Web fallback — sessionStorage is safer than localStorage for tokens.
    try { sessionStorage.setItem(`icaard.secure.${key}`, value); } catch (_) {}
    return;
  }
  await SecureStorage.set(key, value, false, false);
}
export async function secureGet(key) {
  if (!isNative()) {
    try { return sessionStorage.getItem(`icaard.secure.${key}`); } catch (_) { return null; }
  }
  try {
    const v = await SecureStorage.get(key, false, false);
    return typeof v === 'string' ? v : null;
  } catch (_) { return null; }
}
export async function secureDelete(key) {
  if (!isNative()) { try { sessionStorage.removeItem(`icaard.secure.${key}`); } catch (_) {} return; }
  try { await SecureStorage.remove(key); } catch (_) {}
}

// ---------------------------------------------------------------------------
// Biometrics — Face ID / Touch ID / Fingerprint
// ---------------------------------------------------------------------------
export async function biometryAvailable() {
  if (!isNative()) return false;
  try {
    const info = await BiometricAuth.checkBiometry();
    return !!info.isAvailable;
  } catch (_) { return false; }
}
export async function biometryLabel() {
  if (!isNative()) return '';
  try {
    const info = await BiometricAuth.checkBiometry();
    switch (info.biometryType) {
      case BiometryType.faceId: return 'Face ID';
      case BiometryType.touchId: return 'Touch ID';
      case BiometryType.fingerprintAuthentication: return 'Fingerprint';
      default: return 'Biometrics';
    }
  } catch (_) { return 'Biometrics'; }
}
/** Prompt the user to authenticate with their device biometric. */
export async function biometryAuthenticate(reason = 'Unlock iCaard') {
  if (!isNative()) return false;
  try {
    await BiometricAuth.authenticate({
      reason,
      cancelTitle,
      allowDeviceCredential,
      iosFallbackTitle,
      androidTitle,
      androidSubtitle,
      androidConfirmationRequired,
    });
    return true;
  } catch (_) { return false; }
}

// ---------------------------------------------------------------------------
// QR / Barcode scanning
// ---------------------------------------------------------------------------
export async function scanQR() {
  if (!isNative()) return null; // Caller should fall back to html5-qrcode
  const perm = await BarcodeScanner.requestPermissions();
  if (perm.camera !== 'granted' && perm.camera !== 'limited') return null;
  const result = await BarcodeScanner.scan();
  if (result.barcodes.length === 0) return null;
  return result.barcodes[0].rawValue || result.barcodes[0].displayValue || null;
}

// ---------------------------------------------------------------------------
// Camera — profile photos, ID uploads, document uploads
// ---------------------------------------------------------------------------
export async function pickPhoto(source = 'camera') {
  if (!isNative()) return null; // Web should use <input type=file>
  const photo = await Camera.getPhoto({
    quality: 82,
    allowEditing: false,
    resultType: CameraResultType.DataUrl,
    source: source === 'camera' ? CameraSource.Camera : CameraSource.Photos,
    correctOrientation: true,
  });
  return photo.dataUrl || null;
}

// ---------------------------------------------------------------------------
// Share — native share sheet (QR, cards, receipts, tickets, referral links)
// ---------------------------------------------------------------------------
export async function sharePayload(opts) {
  if (isNative()) {
    await Share.share({ title: opts.title, text: opts.text, url: opts.url, files: opts.files });
    return;
  }
  if ((navigator).share) {
    try { await (navigator).share(opts); return; } catch (_) { /* user cancelled */ }
  }
  // Last-ditch fallback: copy the URL and toast.
  await copyToClipboard(opts.url || opts.text || '');
}

// ---------------------------------------------------------------------------
// Clipboard — card numbers, referral codes, wallet IDs
// ---------------------------------------------------------------------------
export async function copyToClipboard(text) {
  if (isNative()) { await Clipboard.write({ string: text }); return; }
  if (navigator.clipboard) { try { await navigator.clipboard.writeText(text); return; } catch (_) {} }
  // Old-school fallback
  const ta = document.createElement('textarea');
  ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
  document.body.appendChild(ta); ta.select();
  try { document.execCommand('copy'); } finally { document.body.removeChild(ta); }
}

// ---------------------------------------------------------------------------
// Filesystem — download cards/receipts/reports
// ---------------------------------------------------------------------------
export async function saveFile(name, base64OrText, mime) {
  if (!isNative()) {
    // Web fallback: trigger a browser download.
    const isBase64 = base64OrText.startsWith('data:') || /^[A-Za-z0-9+/=]+$/.test(base64OrText.slice(0, 32));
    const href = isBase64 && !base64OrText.startsWith('data:') ? `data:${mime};base64,${base64OrText}` : base64OrText;
    const a = document.createElement('a');
    a.href = href; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    return null;
  }
  const isB64 = !base64OrText.startsWith('data:') && !/[<{]/.test(base64OrText.slice(0, 4));
  const res = await Filesystem.writeFile({
    path: name,
    data: isB64 ? base64OrText : base64OrText,
    directory: Directory.Documents,
    encoding: isB64 ? undefined : Encoding.UTF8,
    recursive,
  });
  return res.uri || null;
}

// ---------------------------------------------------------------------------
// Device info — for security logs & user-agent binding
// ---------------------------------------------------------------------------
export async function deviceSignature() {
  if (!isNative()) {
    return {
      platform: 'web',
      user_agent: navigator.userAgent || '',
      language: navigator.language || '',
    };
  }
  try {
    const info = await Device.getInfo();
    const id = await Device.getId();
    return {
      platform: info.platform,
      model: info.model || '',
      os: info.operatingSystem || '',
      os_version: info.osVersion || '',
      manufacturer: info.manufacturer || '',
      is_virtual: String(!!info.isVirtual),
      device_id: id.identifier || '',
    };
  } catch (_) { return { platform: 'unknown' }; }
}

// ---------------------------------------------------------------------------
// Push notifications — device token registration
// ---------------------------------------------------------------------------
export async function registerPush(onToken: (t) => void, onNotification?: (n) => void) {
  if (!isNative()) return;
  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== 'granted') return;
  await PushNotifications.register();
  PushNotifications.addListener('registration', (t) => onToken({ token: t.value, platform: platform() }));
  PushNotifications.addListener('pushNotificationReceived', (n) => onNotification?.(n));
  PushNotifications.addListener('pushNotificationActionPerformed', (a) => {
    const route = (a.notification?.data)?.route;
    if (route) window.location.href = route;
  });
}

// ---------------------------------------------------------------------------
// Haptics — small tactile feedback on important actions
// ---------------------------------------------------------------------------
export async function tapHaptic(kind = 'light') {
  if (!isNative()) return;
  try {
    const map = { light: ImpactStyle.Light, medium: ImpactStyle.Medium, heavy: ImpactStyle.Heavy };
    await Haptics.impact({ style: map[kind] });
  } catch (_) {}
}

// ---------------------------------------------------------------------------
// Native toast — short-lived message anchored to the system UI
// ---------------------------------------------------------------------------
export async function nativeToast(message, duration = 'short') {
  if (!isNative()) return;
  try { await Toast.show({ text: message, duration }); } catch (_) {}
}
